<template>
  <div class="enhanced-analytics">
    <!-- 필터 섹션 -->
    <div class="filter-section mb-4">
      <a-row :gutter="[16, 12]" align="middle" class="filter-row">
        <a-col :xs="24" :sm="24" :md="12" :lg="6" :xl="6">
          <label class="filter-label">
            이벤트
            <span v-if="selectedEventId && !isMobile" style="margin-left: 8px;">
              <a-typography-text
                :copyable="{ text: selectedEventId }"
                style="color: #666; font-size: 12px;"
              >
                (ID: {{ selectedEventId }})
              </a-typography-text>
            </span>
          </label>
          <a-select
            v-model:value="selectedEventId"
            style="width: 100%"
            placeholder="이벤트를 선택해주세요."
            :loading="eventsLoading"
            @change="handleEventChange"
            showSearch
            :filter-option="filterOption"
            allow-clear
          >
            <a-select-option v-for="event in eventList" :key="event._id" :value="event._id">
              {{ event.eventTitle || event.title || event._id }} ({{ getEventStatusText(event.eventStatus || event.status) }})
            </a-select-option>
          </a-select>
        </a-col>

        <a-col :xs="24" :sm="24" :md="12" :lg="7" :xl="7">
          <label class="filter-label">기간 설정</label>
          <a-range-picker
            v-model:value="dateRange"
            style="width: 100%"
            :format="'YYYY-MM-DD'"
            @change="handleDateChange"
          />
        </a-col>

        <a-col :xs="24" :sm="24" :md="24" :lg="11" :xl="11" class="filter-actions">
          <a-space :wrap="isMobile" :class="{ 'mobile-actions': isMobile }">
            <a-button type="primary" @click="fetchAllAnalytics" :loading="loading" class="yellow-button" :block="isMobile">
              <template #icon><ReloadOutlined /></template>
              <span class="btn-text">전체 분석 조회</span>
            </a-button>
            <a-tooltip title="모든 필터 초기화">
              <a-button @click="resetFilters" :block="isMobile">
                <template #icon><i class="fa fa-eraser"></i></template>
                <span class="btn-text">필터 초기화</span>
              </a-button>
            </a-tooltip>
            <a-button
              v-if="selectedEventId"
              @click="goToEditEvent"
              :block="isMobile"
              class="yellow-button"
            >
              <template #icon><i class="fa fa-edit"></i></template>
              <span class="btn-text">이벤트 수정</span>
            </a-button>
          </a-space>
        </a-col>
      </a-row>
    </div>

    <!-- 분석 카드 그리드 -->
    <a-row :gutter="[16, 16]">
      <!-- 바이럴 분석 (K-Factor) 카드 -->
      <a-col :xs="24" :sm="24" :md="24" :lg="12" :xl="12">
        <a-card :loading="viralLoading" class="analytics-card">
          <template #title>
            <span>바이럴 분석 (K-Factor)</span>
            <a-tooltip>
              <template #title>
                <div style="max-width: 300px;">
                  <p><strong>K-Factor란?</strong></p>
                  <p>바이럴 계수로, 한 사용자가 평균적으로 몇 명의 새로운 사용자를 유입시키는지를 나타내는 지표입니다.</p>
                  <p style="margin-top: 8px;"><strong>계산 방법:</strong></p>
                  <p>K = (사용자당 평균 공유 수) × (공유를 통한 전환율)</p>
                  <p style="margin-top: 8px;"><strong>해석:</strong></p>
                  <ul style="margin: 0; padding-left: 16px;">
                    <li>K > 1: 바이럴 성장 (사용자가 기하급수적으로 증가)</li>
                    <li>K = 1: 안정적 유지</li>
                    <li>K &lt; 1: 점진적 감소</li>
                  </ul>
                  <p style="margin-top: 8px;"><strong>예시:</strong></p>
                  <p>K=1.5 → 10명의 사용자가 15명의 신규 사용자를 유입</p>
                </div>
              </template>
              <QuestionCircleOutlined style="font-size: 14px; color: #999; margin-left: 6px; cursor: help;" />
            </a-tooltip>
          </template>
          <template #extra>
            <a-button type="link" size="small" @click="fetchViralAnalytics" :loading="viralLoading">
              <ReloadOutlined />
            </a-button>
          </template>

          <div v-if="viralData">
            <!-- 1행: K-Factor, 총 공유 수 -->
            <a-row :gutter="[16, 12]" class="mb-3">
              <a-col :xs="12" :sm="12" :md="12">
                <a-statistic
                  :value="viralData.currentPeriod?.kFactor || 0"
                  :precision="2"
                  :value-style="getKFactorStyle(viralData.currentPeriod?.kFactor)"
                >
                  <template #title>
                    <span>K-Factor</span>
                    <a-tooltip title="K > 1이면 바이럴 성장, K < 1이면 점진적 감소">
                      <QuestionCircleOutlined style="font-size: 12px; color: #999; margin-left: 4px;" />
                    </a-tooltip>
                  </template>
                </a-statistic>
              </a-col>
              <a-col :xs="12" :sm="12" :md="12">
                <a-statistic
                  :value="viralData.currentPeriod?.totalShares || 0"
                  :value-style="{ color: '#1890ff' }"
                >
                  <template #title>
                    <span>총 공유 수</span>
                    <a-tooltip title="eventmetrics에서 event='SHARE'인 이벤트의 총 건수. 카카오 공유, 링크 복사 등 모든 공유 액션을 포함합니다.">
                      <QuestionCircleOutlined style="font-size: 12px; color: #999; margin-left: 4px;" />
                    </a-tooltip>
                  </template>
                </a-statistic>
              </a-col>
            </a-row>

            <!-- 2행: 페이지 방문자 → 참여완료 → 미응모 이탈 (전환 퍼널) -->
            <a-row :gutter="[8, 8]" class="mb-2" v-if="viralData.currentPeriod?.totalRegisteredAttendees !== undefined">
              <a-col :span="24">
                <div style="background: #f6ffed; border: 1px solid #b7eb8f; border-radius: 6px; padding: 16px;">
                  <div style="display: flex; align-items: center; justify-content: space-between; flex-wrap: wrap; gap: 12px;">
                    <!-- 페이지 방문자 -->
                    <div style="text-align: center; flex: 1; min-width: 100px;">
                      <div style="font-size: 12px; color: #666; margin-bottom: 4px;">
                        페이지 방문자
                        <a-tooltip>
                          <template #title>
                            <div style="max-width: 300px;">
                              <p><strong>이벤트 페이지에 방문한 전체 고유 사용자 수</strong></p>
                              <p style="margin-top: 6px;">구성:</p>
                              <ul style="margin: 4px 0 0 0; padding-left: 16px; font-size: 11px;">
                                <li><strong>인증서 수신자</strong>: 알림톡으로 직접 유입</li>
                                <li><strong>공유 링크 유입</strong>: 친구가 공유한 링크로 유입</li>
                              </ul>
                              <p style="margin-top: 6px; font-size: 11px; color: #1890ff;">= 참여완료 + 미응모 이탈</p>
                            </div>
                          </template>
                          <QuestionCircleOutlined style="font-size: 11px; color: #999; margin-left: 2px;" />
                        </a-tooltip>
                      </div>
                      <div style="font-size: 22px; font-weight: 600; color: #1890ff;">
                        {{ (viralData.currentPeriod?.totalPageVisitors || 0).toLocaleString() }}
                      </div>
                    </div>

                    <!-- 화살표 -->
                    <div style="color: #52c41a; font-size: 18px;">→</div>

                    <!-- 참여완료 -->
                    <div style="text-align: center; flex: 1; min-width: 100px;">
                      <div style="font-size: 12px; color: #666; margin-bottom: 4px;">
                        참여완료
                        <a-tooltip>
                          <template #title>
                            <div style="max-width: 280px;">
                              <p><strong>eventmetrics 기준 응모 완료 고유 사용자 수</strong></p>
                              <p style="margin-top: 4px; font-size: 11px; color: #999;">attendeeId 기준 고유 집계 (세대별 사용자 분포 합계와 동일)</p>
                            </div>
                          </template>
                          <QuestionCircleOutlined style="font-size: 11px; color: #999; margin-left: 2px;" />
                        </a-tooltip>
                      </div>
                      <div style="font-size: 22px; font-weight: 600; color: #52c41a;">
                        {{ (viralData.currentPeriod?.totalNewUsers || 0).toLocaleString() }}
                      </div>
                      <div style="font-size: 11px; color: #52c41a;">
                        (전환율 {{ viralData.currentPeriod?.totalPageVisitors > 0 ? ((viralData.currentPeriod?.totalNewUsers / viralData.currentPeriod?.totalPageVisitors) * 100).toFixed(1) : 0 }}%)
                      </div>
                    </div>

                    <!-- 화살표 -->
                    <div style="color: #ff7875; font-size: 18px;">→</div>

                    <!-- 미응모 이탈 -->
                    <div style="text-align: center; flex: 1; min-width: 100px;">
                      <div style="font-size: 12px; color: #666; margin-bottom: 4px;">
                        미응모 이탈
                        <a-tooltip>
                          <template #title>
                            <div style="max-width: 280px;">
                              <p><strong>페이지 방문 후 응모하지 않고 이탈한 사용자</strong></p>
                              <p style="margin-top: 4px; font-size: 11px; color: #999;">친구 링크로 접속했으나 응모 완료하지 않은 고유 방문자 수 (sessionId 기준)</p>
                            </div>
                          </template>
                          <QuestionCircleOutlined style="font-size: 11px; color: #999; margin-left: 2px;" />
                        </a-tooltip>
                      </div>
                      <div style="font-size: 22px; font-weight: 600; color: #ff7875;">
                        {{ (viralData.currentPeriod?.totalDroppedVisitors || 0).toLocaleString() }}
                      </div>
                      <div style="font-size: 11px; color: #ff7875;">
                        (이탈율 {{ viralData.currentPeriod?.totalPageVisitors > 0 ? ((viralData.currentPeriod?.totalDroppedVisitors / viralData.currentPeriod?.totalPageVisitors) * 100).toFixed(1) : 0 }}%)
                      </div>
                    </div>
                  </div>
                </div>
              </a-col>
            </a-row>

            <a-divider style="margin: 12px 0;" />

            <div class="virality-indicator">
              <span>바이럴 수준: </span>
              <a-tag :color="getViralityColor(viralData.currentPeriod?.virality)">
                {{ getViralityText(viralData.currentPeriod?.virality) }}
              </a-tag>
            </div>

            <!-- 세대별 분포 -->
            <div v-if="viralData.currentPeriod?.generationData?.length > 0" class="mt-3">
              <h4 style="font-size: 13px; color: #666; margin-bottom: 8px;">
                세대별 사용자 분포
                <a-tooltip>
                  <template #title>
                    <div style="max-width: 280px;">
                      <p><strong>세대(Generation)란?</strong></p>
                      <p>바이럴 전파의 깊이를 나타내는 지표입니다.</p>
                      <p style="margin-top: 8px;"><strong>세대별 의미:</strong></p>
                      <ul style="margin: 0; padding-left: 16px;">
                        <li><strong>0세대:</strong> 최초 공유자 (직접 유입)</li>
                        <li><strong>1세대:</strong> 0세대가 공유해서 온 사람</li>
                        <li><strong>2세대:</strong> 1세대가 공유해서 온 사람</li>
                        <li><strong>N세대:</strong> N-1세대가 공유해서 온 사람</li>
                      </ul>
                      <p style="margin-top: 8px;"><strong>해석:</strong></p>
                      <p>세대가 높을수록 바이럴이 더 멀리 전파된 것을 의미합니다.</p>
                    </div>
                  </template>
                  <QuestionCircleOutlined style="font-size: 12px; color: #999; margin-left: 4px; cursor: help;" />
                </a-tooltip>
              </h4>
              <a-table
                :columns="generationColumns"
                :data-source="viralData.currentPeriod.generationData"
                :pagination="false"
                size="small"
                :rowKey="record => record.depth"
              />
            </div>
          </div>
          <a-empty v-else description="데이터가 없습니다" />
        </a-card>
      </a-col>

      <!-- 채널 성과 분석 카드 -->
      <a-col :xs="24" :sm="24" :md="24" :lg="12" :xl="12">
        <a-card :loading="channelLoading" class="analytics-card">
          <template #title>
            <span>채널별 성과 분석</span>
            <a-tooltip>
              <template #title>
                <div style="max-width: 280px;">
                  <p><strong>공유 채널이란?</strong></p>
                  <p>사용자가 이벤트 링크를 공유한 채널을 분석합니다.</p>
                  <p style="margin-top: 8px;"><strong>채널 종류:</strong></p>
                  <ul style="margin: 0; padding-left: 16px;">
                    <li><strong>1:1 대화:</strong> 카카오톡 개인 대화</li>
                    <li><strong>단체 대화:</strong> 카카오톡 그룹 채팅</li>
                    <li><strong>나에게 보내기:</strong> 카카오톡 나와의 채팅</li>
                    <li><strong>알림톡/친구톡 링크:</strong> 직접 URL 접속</li>
                  </ul>
                </div>
              </template>
              <QuestionCircleOutlined style="font-size: 14px; color: #999; margin-left: 6px; cursor: help;" />
            </a-tooltip>
          </template>
          <template #extra>
            <a-button type="link" size="small" @click="fetchChannelPerformance" :loading="channelLoading">
              <ReloadOutlined />
            </a-button>
          </template>

          <div v-if="channelData?.currentPeriod?.channels?.length > 0">
            <!-- 최고/최저 성과 채널 -->
            <a-row :gutter="[16, 12]" class="mb-3">
              <a-col :xs="24" :sm="12" :md="12">
                <a-statistic
                  :value="channelData.currentPeriod.topPerformingChannel?.conversionRate || 0"
                  :precision="1"
                  suffix="%"
                  :value-style="{ color: '#52c41a' }"
                >
                  <template #title>
                    <span>최고 성과: {{ channelData.currentPeriod.topPerformingChannel?.name || '-' }}</span>
                    <a-tooltip title="전환율이 가장 높은 채널. 전환율 = (result='SUCCESS' 이벤트 수 / 총 이벤트 수) * 100">
                      <QuestionCircleOutlined style="font-size: 12px; color: #999; margin-left: 4px;" />
                    </a-tooltip>
                  </template>
                </a-statistic>
              </a-col>
              <a-col :xs="24" :sm="12" :md="12">
                <a-statistic
                  :value="channelData.currentPeriod.totalConversions || 0"
                  :value-style="{ color: '#1890ff' }"
                >
                  <template #title>
                    <span>총 전환 수</span>
                    <a-tooltip title="모든 채널에서 result='SUCCESS'인 이벤트의 총 건수. 목표를 달성한 사용자 액션 수입니다.">
                      <QuestionCircleOutlined style="font-size: 12px; color: #999; margin-left: 4px;" />
                    </a-tooltip>
                  </template>
                </a-statistic>
              </a-col>
            </a-row>

            <a-divider style="margin: 12px 0;" />

            <!-- 채널별 테이블 -->
            <a-table
              :columns="channelColumns"
              :data-source="channelData.currentPeriod.channels"
              :pagination="false"
              size="small"
              :rowKey="record => record.channel"
            >
              <template #channel="{ record }">
                <a-tag :color="getChannelColor(record.channel)">
                  {{ record.channel }}
                </a-tag>
              </template>
              <template #conversionRate="{ record }">
                <span :style="{ color: record.conversionRate > 50 ? '#52c41a' : '#666' }">
                  {{ record.conversionRate?.toFixed(1) }}%
                </span>
              </template>
            </a-table>
          </div>
          <a-empty v-else description="데이터가 없습니다" />
        </a-card>
      </a-col>

      <!-- 추천인 성과 분석 카드 -->
      <a-col :span="24">
        <a-card :loading="referrerLoading" class="analytics-card">
          <template #title>
            <span>추천인 성과 분석</span>
            <a-tooltip>
              <template #title>
                <div style="max-width: 300px;">
                  <p><strong>추천인(Referrer)이란?</strong></p>
                  <p>이벤트 링크를 다른 사람에게 공유한 참여자입니다.</p>
                  <p style="margin-top: 8px;"><strong>분석 항목:</strong></p>
                  <ul style="margin: 0; padding-left: 16px;">
                    <li><strong>유입:</strong> 해당 추천인 링크를 통해 방문한 고유 사용자 수</li>
                    <li><strong>응모 완료:</strong> 유입된 사용자 중 실제 응모를 완료한 수</li>
                    <li><strong>응모율:</strong> 유입 대비 응모 완료 비율 (최대 100%)</li>
                    <li><strong>2차 공유:</strong> 유입된 사용자가 다시 공유한 횟수</li>
                  </ul>
                  <p style="margin-top: 8px;"><strong>활용:</strong></p>
                  <p>상위 추천인에게 추가 인센티브를 제공하여 바이럴 확대</p>
                </div>
              </template>
              <QuestionCircleOutlined style="font-size: 14px; color: #999; margin-left: 6px; cursor: help;" />
            </a-tooltip>
          </template>
          <template #extra>
            <a-button type="link" size="small" @click="fetchReferrerPerformance" :loading="referrerLoading">
              <ReloadOutlined />
            </a-button>
          </template>

          <div v-if="referrerData?.topReferrers?.length > 0">
            <!-- 요약 통계 -->
            <a-row :gutter="[16, 12]" class="mb-3">
              <a-col :xs="12" :sm="12" :md="6" :lg="6">
                <a-statistic
                  :value="referrerData.totalReferrers || 0"
                >
                  <template #title>
                    <span>총 추천인 수</span>
                    <a-tooltip title="이벤트 링크를 공유한 참여자 수">
                      <QuestionCircleOutlined style="font-size: 12px; color: #999; margin-left: 4px;" />
                    </a-tooltip>
                  </template>
                </a-statistic>
              </a-col>
              <a-col :xs="12" :sm="12" :md="6" :lg="6">
                <a-statistic
                  :value="referrerData.avgConversionRate || 0"
                  :precision="1"
                  suffix="%"
                  :value-style="{ color: '#1890ff' }"
                >
                  <template #title>
                    <span>평균 응모율</span>
                    <a-tooltip title="추천인별 평균 응모율 (유입 대비 응모 완료 비율)">
                      <QuestionCircleOutlined style="font-size: 12px; color: #999; margin-left: 4px;" />
                    </a-tooltip>
                  </template>
                </a-statistic>
              </a-col>
              <a-col :xs="24" :sm="24" :md="12" :lg="12">
                <a-statistic
                  title="분석 기간"
                  :value="formatDateRange(referrerData.dateRange)"
                  :value-style="{ fontSize: '14px', color: '#666' }"
                />
              </a-col>
            </a-row>

            <a-divider style="margin: 12px 0;" />

            <!-- 추천인 테이블 -->
            <a-table
              :columns="referrerColumns"
              :data-source="referrerData.topReferrers"
              :pagination="referrerPagination"
              size="small"
              :scroll="{ y: 400 }"
              :rowKey="record => record.referrerId"
              @change="handleReferrerTableChange"
            >
              <template #conversionRate="{ record }">
                <a-progress
                  :percent="Math.min(record.conversionRate || 0, 100)"
                  :stroke-color="record.conversionRate > 50 ? '#52c41a' : '#1890ff'"
                  size="small"
                  style="width: 120px;"
                  :format="percent => `${(record.conversionRate || 0).toFixed(1)}%`"
                />
              </template>
            </a-table>
          </div>
          <a-empty v-else description="추천인 데이터가 없습니다" />
        </a-card>
      </a-col>

      <!-- 퍼널 분석 카드 -->
      <a-col :span="24">
        <a-card :loading="funnelLoading" class="analytics-card funnel-card">
          <template #title>
            <span>사용자 여정 퍼널 분석</span>
            <a-tooltip>
              <template #title>
                <div style="max-width: 300px;">
                  <p><strong>퍼널(Funnel) 분석이란?</strong></p>
                  <p>사용자가 목표를 달성하기까지의 여정을 단계별로 분석합니다.</p>
                  <p style="margin-top: 8px;"><strong>주요 지표:</strong></p>
                  <ul style="margin: 0; padding-left: 16px;">
                    <li><strong>전환율:</strong> 이전 단계 대비 다음 단계로 진행한 비율</li>
                    <li><strong>이탈 수:</strong> 해당 단계에서 다음으로 진행하지 않은 수</li>
                  </ul>
                  <p style="margin-top: 8px;"><strong>활용:</strong></p>
                  <p>이탈이 많은 단계를 파악하여 UX 개선에 활용</p>
                </div>
              </template>
              <QuestionCircleOutlined style="font-size: 14px; color: #999; margin-left: 6px; cursor: help;" />
            </a-tooltip>
          </template>
          <template #extra>
            <a-button type="link" size="small" @click="fetchFunnelAnalysis" :loading="funnelLoading">
              <ReloadOutlined />
            </a-button>
          </template>

          <div v-if="funnelData?.funnels?.[0]?.steps?.length > 0">
            <!-- 퍼널 유형 탭 -->
            <a-tabs v-model:activeKey="activeFunnelTab" class="funnel-tabs" @change="handleFunnelTabChange">
              <a-tab-pane key="certificate" tab="인증서 알림톡 전환">
                <template #tab>
                  <span class="tab-label">
                    <i class="fas fa-certificate" style="margin-right: 6px;"></i>
                    인증서 알림톡 전환 ({{ certificateFunnelSteps.length }}단계)
                  </span>
                </template>
              </a-tab-pane>
              <a-tab-pane key="patient" tab="환자 이벤트페이지 전환">
                <template #tab>
                  <span class="tab-label">
                    <i class="fas fa-user-md" style="margin-right: 6px;"></i>
                    환자 이벤트페이지 전환 ({{ patientFunnelSteps.length }}단계)
                  </span>
                </template>
              </a-tab-pane>
              <a-tab-pane key="friend" tab="친구 이벤트페이지 전환">
                <template #tab>
                  <span class="tab-label">
                    <i class="fas fa-users" style="margin-right: 6px;"></i>
                    친구 이벤트페이지 전환 ({{ friendFunnelSteps.length }}단계)
                  </span>
                </template>
              </a-tab-pane>
              <a-tab-pane key="reward" tab="쿠폰 발급/수령">
                <template #tab>
                  <span class="tab-label reward-tab-label">
                    <i class="fas fa-ticket-alt" style="margin-right: 6px;"></i>
                    쿠폰 발급/수령 ({{ rewardFunnelSteps.length }}건)
                  </span>
                </template>
              </a-tab-pane>
            </a-tabs>
            <!-- 전체 전환율 요약 -->
            <a-row :gutter="[16, 12]" class="mb-3">
              <a-col :xs="12" :sm="12" :md="6" :lg="6">
                <a-statistic
                  :value="currentFunnelSummary.totalStartingUsers"
                >
                  <template #title>
                    <span>시작 사용자</span>
                    <a-tooltip title="퍼널 첫 번째 단계의 고유 사용자(uniqueUsers) 수. 해당 여정을 시작한 총 사용자입니다.">
                      <QuestionCircleOutlined style="font-size: 12px; color: #999; margin-left: 4px;" />
                    </a-tooltip>
                  </template>
                </a-statistic>
              </a-col>
              <a-col :xs="12" :sm="12" :md="6" :lg="6">
                <a-statistic
                  :value="currentFunnelSummary.overallConversionRate"
                  :precision="1"
                  suffix="%"
                  :value-style="{ color: '#52c41a' }"
                >
                  <template #title>
                    <span>전체 전환율</span>
                    <a-tooltip title="(마지막 단계 사용자 / 첫 단계 사용자) * 100. 전체 여정을 완료한 비율입니다.">
                      <QuestionCircleOutlined style="font-size: 12px; color: #999; margin-left: 4px;" />
                    </a-tooltip>
                  </template>
                </a-statistic>
              </a-col>
              <a-col :xs="24" :sm="24" :md="12" :lg="12">
                <a-statistic
                  :value="currentFunnelSummary.biggestDropoffStep?.dropoffRate || 0"
                  suffix="명 이탈"
                  :value-style="{ color: '#ff4d4f' }"
                >
                  <template #title>
                    <span>가장 큰 이탈: {{ currentFunnelSummary.biggestDropoffStep?.step || '-' }}</span>
                    <a-tooltip title="이탈 수가 가장 많은 단계. 이탈 수 = 이전 단계 사용자 - 현재 단계 사용자. Fallback/보상 단계는 이탈로 계산하지 않습니다.">
                      <QuestionCircleOutlined style="font-size: 12px; color: #999; margin-left: 4px;" />
                    </a-tooltip>
                  </template>
                </a-statistic>
              </a-col>
            </a-row>

            <a-divider style="margin: 12px 0;" />

            <!-- 모바일 전용 퍼널 카드 레이아웃 -->
            <div class="funnel-mobile-cards">
              <div
                v-for="(step, index) in currentFunnelSteps"
                :key="`mobile-${step.stepName}`"
                class="funnel-step-card"
                :class="{ 'first-step': index === 0, 'fallback-step': step.isFallback, 'reward-step': step.isReward }"
              >
                <div class="step-card-header">
                  <span class="step-number">{{ index + 1 }}</span>
                  <a-tooltip placement="top">
                    <template #title>
                      <div style="font-family: monospace; font-size: 11px;">{{ step.stepName }}</div>
                      <div v-if="step.breakdown?.length" style="margin-top: 4px;">
                        <strong>상세:</strong>
                        <div v-for="b in step.breakdown" :key="b.channel">
                          {{ b.description || b.channel }}: {{ b.count }}건
                        </div>
                      </div>
                    </template>
                    <span class="step-name">
                      {{ getStepDisplayName(step.stepName, step.pageDescription) }}
                      <span v-if="step.isFallback" class="fallback-tag">대체발송</span>
                      <span v-if="step.isReward" class="reward-tag">보상</span>
                    </span>
                  </a-tooltip>
                </div>
                <div class="step-card-body">
                  <div class="step-users">
                    <span class="users-count">{{ step.uniqueUsers.toLocaleString() }}</span>
                    <span class="users-label">명</span>
                  </div>
                  <div v-if="index > 0" class="step-metrics">
                    <div class="conversion-badge" :class="{ 'low': step.conversionRate < 50, 'fallback': step.isFallback, 'reward': step.isReward }">
                      {{ step.isFallback ? '대체 성공' : (step.isReward ? '보상 발송' : '전환율') }} {{ step.conversionRate?.toFixed(1) }}%
                    </div>
                    <div v-if="step.dropoffCount > 0 && !step.isFallback && !step.isReward" class="dropoff-badge">
                      -{{ step.dropoffCount }}명 이탈
                    </div>
                    <div v-if="step.averageDuration > 0" class="duration-badge">
                      <i class="fas fa-clock"></i> {{ step.averageDuration.toFixed(1) }}초
                    </div>
                    <div v-if="step.isFallback" class="fallback-badge">
                      <i class="fas fa-sync-alt"></i> Fallback
                    </div>
                    <div v-if="step.isReward" class="reward-badge">
                      <i class="fas fa-gift"></i> Reward
                    </div>
                  </div>
                  <div v-else class="step-metrics">
                    <div class="start-badge">시작점</div>
                    <div v-if="step.averageDuration > 0" class="duration-badge">
                      <i class="fas fa-clock"></i> {{ step.averageDuration.toFixed(1) }}초
                    </div>
                  </div>
                </div>
                <!-- 다음 단계로의 화살표 (마지막 아님) -->
                <div v-if="index < currentFunnelSteps.length - 1" class="step-arrow">
                  <i class="fas fa-chevron-down"></i>
                </div>
              </div>
            </div>

            <!-- PC/태블릿용 트라페지움 퍼널 시각화 -->
            <div class="trapezium-funnel-container desktop-only">
              <!-- 왼쪽 단계명 패널 -->
              <div class="funnel-labels-panel">
                <div
                  v-for="(step, index) in currentFunnelSteps"
                  :key="`label-${step.stepName}`"
                  class="funnel-label-row"
                  :class="{ 'fallback-label-row': step.isFallback, 'reward-label-row': step.isReward }"
                  :style="{ height: `${funnelConfig.stepHeight + funnelConfig.gap}px` }"
                >
                  <span class="step-index" :class="{ 'fallback-index': step.isFallback, 'reward-index': step.isReward }">{{ index + 1 }}</span>
                  <a-tooltip placement="left">
                    <template #title>
                      <div>
                        <div><strong>원본 이벤트명:</strong></div>
                        <div style="font-family: monospace; font-size: 11px;">{{ step.stepName }}</div>
                        <div v-if="step.pageDescription && step.pageDescription !== step.stepName" style="margin-top: 4px;">
                          <strong>설명:</strong> {{ step.pageDescription }}
                        </div>
                        <div v-if="step.isFallback" style="margin-top: 4px; color: #722ed1;">
                          <strong>타입:</strong> Fallback (대체 발송 성공)
                        </div>
                        <div v-if="step.isReward" style="margin-top: 4px; color: #d48806;">
                          <strong>타입:</strong> Reward (보상 이벤트)
                        </div>
                        <div v-if="step.breakdown?.length" style="margin-top: 4px;">
                          <strong>상세:</strong>
                          <div v-for="b in step.breakdown" :key="b.channel">
                            {{ b.description || b.channel }}: {{ b.count }}건
                          </div>
                        </div>
                      </div>
                    </template>
                    <span class="step-label" :class="{ 'step-label-first': index === 0, 'step-label-fallback': step.isFallback, 'step-label-reward': step.isReward }">
                      {{ getStepDisplayName(step.stepName, step.pageDescription) }}
                      <span v-if="step.isFallback" class="fallback-tag-desktop">대체</span>
                      <span v-if="step.isReward" class="reward-tag-desktop">보상</span>
                    </span>
                  </a-tooltip>
                  <span class="step-connector"></span>
              </div>
              </div>

              <!-- 중앙 트라페지움 퍼널 -->
              <div class="funnel-chart-wrapper">
                <svg
                  :width="funnelConfig.width"
                  :height="getFunnelHeight(currentFunnelSteps.length)"
                  class="funnel-svg"
                >
                  <defs>
                    <!-- 그라데이션 정의 -->
                    <linearGradient
                      v-for="(step, index) in currentFunnelSteps"
                      :key="`grad-${index}`"
                      :id="`funnel-gradient-${index}`"
                      x1="0%" y1="0%" x2="100%" y2="0%"
                    >
                      <stop offset="0%" :style="`stop-color:${getFunnelColor(index, currentFunnelSteps.length)};stop-opacity:0.9`" />
                      <stop offset="100%" :style="`stop-color:${getFunnelColor(index, currentFunnelSteps.length)};stop-opacity:1`" />
                    </linearGradient>
                  </defs>

                  <!-- 각 단계별 트라페지움 -->
                  <g v-for="(step, index) in currentFunnelSteps" :key="step.stepName">
                    <!-- 트라페지움 shape -->
                    <polygon
                      :points="getTrapeziumPoints(index, step, currentFunnelSteps)"
                      :fill="`url(#funnel-gradient-${index})`"
                      class="funnel-trapezium"
                    />

                    <!-- 단계 번호 (왼쪽) -->
                    <circle
                      :cx="30"
                      :cy="getTrapeziumCenterY(index)"
                      r="12"
                      :fill="index === 0 ? '#52c41a' : '#1890ff'"
                      class="step-circle"
                    />
                    <text
                      :x="30"
                      :y="getTrapeziumCenterY(index)"
                      class="step-number-svg"
                      text-anchor="middle"
                      dominant-baseline="middle"
                    >
                      {{ index + 1 }}
                    </text>

                    <!-- 사용자 수 (중앙) -->
                    <text
                      :x="funnelConfig.width / 2"
                      :y="getTrapeziumCenterY(index)"
                      class="funnel-users"
                      text-anchor="middle"
                      dominant-baseline="middle"
                    >
                      {{ step.uniqueUsers.toLocaleString() }}명
                    </text>
                  </g>
                </svg>
              </div>

              <!-- 오른쪽 전환율 정보 패널 -->
              <div class="funnel-metrics-panel">
                <div
                  v-for="(step, index) in currentFunnelSteps"
                  :key="`metrics-${step.stepName}`"
                  class="funnel-metric-row"
                  :class="{ 'fallback-metric-row': step.isFallback, 'reward-metric-row': step.isReward }"
                  :style="{ height: `${funnelConfig.stepHeight + funnelConfig.gap}px` }"
                >
                  <template v-if="index > 0">
                    <div class="metric-conversion">
                      <span class="metric-label">{{ step.isFallback ? '대체' : (step.isReward ? '보상' : '전환율') }}</span>
                      <span class="metric-value" :class="{ 'fallback-value': step.isFallback, 'reward-value': step.isReward }">
                        {{ step.conversionRate?.toFixed(1) }}%
                      </span>
                    </div>
                    <div class="metric-detail">
                      <span class="metric-fraction">
                        ({{ step.uniqueUsers }}/{{ step.referenceUsers || currentFunnelSteps[index - 1]?.uniqueUsers || 0 }})
                      </span>
                    </div>
                    <div v-if="step.dropoffCount > 0 && !step.isFallback && !step.isReward" class="metric-dropoff">
                      <span class="metric-value dropoff">-{{ step.dropoffCount }}명 이탈</span>
                    </div>
                    <div v-if="step.averageDuration > 0" class="metric-duration">
                      <i class="fas fa-clock" style="margin-right: 4px; font-size: 10px;"></i>
                      <span class="metric-value duration">{{ step.averageDuration.toFixed(1) }}초</span>
                    </div>
                    <div v-if="step.isFallback" class="metric-fallback-info">
                      <i class="fas fa-sync-alt"></i>
                      <span>대체 발송 성공</span>
                    </div>
                    <div v-if="step.isReward" class="metric-reward-info">
                      <i class="fas fa-gift"></i>
                      <span>보상 이벤트</span>
                    </div>
                  </template>
                  <template v-else>
                    <div class="metric-conversion">
                      <span class="metric-label">시작</span>
                      <span class="metric-value start">{{ step.uniqueUsers.toLocaleString() }}명</span>
                    </div>
                    <div v-if="step.averageDuration > 0" class="metric-duration">
                      <i class="fas fa-clock" style="margin-right: 4px; font-size: 10px;"></i>
                      <span class="metric-value duration">{{ step.averageDuration.toFixed(1) }}초</span>
                    </div>
                  </template>
                </div>
              </div>

              <!-- 우측 페이지 내 액션 버튼 -->
              <div class="branching-actions-panel-right">
                <div
                  v-for="(step, index) in currentFunnelSteps"
                  :key="`actions-${step.stepName}`"
                  class="branching-metric-row"
                  :style="{ height: `${funnelConfig.stepHeight + funnelConfig.gap}px` }"
                >
                  <template v-if="step.branchingActions?.length">
                    <a-button
                      type="link"
                      size="small"
                      class="branching-toggle-btn-right"
                      :class="{ 'active': isBranchingExpanded(step.stepName) }"
                      @click="toggleBranchingActions(step.stepName)"
                    >
                      <i :class="isBranchingExpanded(step.stepName) ? 'fas fa-chevron-up' : 'fas fa-chevron-down'" style="margin-right: 4px;"></i>
                      페이지 내 액션 ({{ step.branchingActions.length }})
                    </a-button>
                  </template>
                </div>
              </div>
            </div>

            <!-- 하단 확장 패널: 페이지 내 액션 상세 + 미니 퍼널 -->
            <div
              v-for="step in currentFunnelSteps.filter(s => s.branchingActions?.length && isBranchingExpanded(s.stepName))"
              :key="`detail-${step.stepName}`"
              class="branching-detail-section"
            >
              <div class="branching-detail-header">
                <div class="branching-detail-title">
                  <i class="fas fa-code-branch"></i>
                  <span>{{ getStepDisplayName(step.stepName, step.pageDescription) }} - 페이지 내 액션</span>
                  <span class="branching-detail-ref">(기준: {{ step.uniqueUsers.toLocaleString() }}명)</span>
                </div>
                <a-button type="text" size="small" class="branching-close-btn" @click="toggleBranchingActions(step.stepName)">
                  <i class="fas fa-times"></i>
                </a-button>
              </div>
              <div class="branching-detail-content">
                <!-- 왼쪽: 액션 선택 목록 -->
                <div class="action-selector-panel">
                  <div class="action-selector-title">액션 선택</div>
                  <div class="action-selector-list">
                    <div
                      v-for="(action, actionIdx) in step.branchingActions"
                      :key="action.stepName"
                      class="action-selector-item"
                      :class="{ 'selected': isActionSelected(step.stepName, action.stepName) }"
                    >
                      <a-checkbox
                        :checked="isActionSelected(step.stepName, action.stepName)"
                        @change="toggleActionSelection(step.stepName, action.stepName)"
                      />
                      <span class="action-selector-name">{{ action.pageDescription || action.targetName }}</span>
                      <div class="action-selector-bar-wrapper">
                        <div
                          class="action-selector-bar"
                          :class="{
                            'high-rate': action.conversionRate >= 50,
                            'mid-rate': action.conversionRate >= 20 && action.conversionRate < 50,
                            'low-rate': action.conversionRate < 20,
                            'non-conversion': action.isNonConversionAction
                          }"
                          :style="{ width: `${Math.min(action.conversionRate || 0, 100)}%` }"
                        ></div>
                      </div>
                      <span class="action-selector-stats">
                        {{ action.uniqueUsers.toLocaleString() }}명
                        <span class="action-selector-rate">({{ action.conversionRate?.toFixed(1) }}%)</span>
                        <span v-if="action.isNonConversionAction" class="exit-badge">이탈</span>
                      </span>
                    </div>
                  </div>
                </div>
                <!-- 오른쪽: 미니 퍼널 차트 -->
                <div class="mini-funnel-panel">
                  <div class="mini-funnel-title">선택된 액션 퍼널</div>
                  <div v-if="getSelectedActionsForStep(step.stepName).length > 0" class="mini-funnel-chart">
                    <!-- 시작점: 부모 단계 -->
                    <div class="mini-funnel-step start-step">
                      <div class="mini-step-bar-wrapper">
                        <div class="mini-step-bar start" style="width: 100%;"></div>
                      </div>
                      <div class="mini-step-info">
                        <span class="mini-step-name">{{ getStepDisplayName(step.stepName, step.pageDescription) }}</span>
                        <span class="mini-step-users">{{ step.uniqueUsers.toLocaleString() }}명</span>
                      </div>
                    </div>
                    <!-- 선택된 액션들 -->
                    <div
                      v-for="(action, idx) in getSelectedActionsForStep(step.stepName)"
                      :key="action.stepName"
                      class="mini-funnel-step"
                    >
                      <div class="mini-step-connector">
                        <span class="mini-step-rate" :class="getRateClass(action.conversionRate)">
                          {{ action.conversionRate?.toFixed(1) }}%
                        </span>
                        <i class="fas fa-arrow-down"></i>
                      </div>
                      <div class="mini-step-bar-wrapper">
                        <div
                          class="mini-step-bar"
                          :class="getRateClass(action.conversionRate)"
                          :style="{ width: `${Math.min(action.conversionRate || 0, 100)}%` }"
                        ></div>
                      </div>
                      <div class="mini-step-info">
                        <span class="mini-step-name">{{ action.pageDescription || action.targetName }}</span>
                        <span class="mini-step-users">{{ action.uniqueUsers.toLocaleString() }}명</span>
                      </div>
                    </div>
                  </div>
                  <div v-else class="mini-funnel-empty">
                    <i class="fas fa-hand-pointer"></i>
                    <span>왼쪽에서 액션을 선택하면<br/>미니 퍼널이 생성됩니다</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <a-empty v-else description="퍼널 데이터가 없습니다" />
        </a-card>
      </a-col>
    </a-row>
  </div>
</template>

<script>
import { ref, computed, onMounted, onUnmounted, watch, h } from 'vue';
import { Tooltip } from 'ant-design-vue';
import { message } from 'ant-design-vue';
import { useStore } from 'vuex';
import { useRoute, useRouter } from 'vue-router';
import {
  ReloadOutlined,
  InfoCircleOutlined,
  QuestionCircleOutlined,
} from '@ant-design/icons-vue';
import dayjs from 'dayjs';
import eventsApis from '../../api';
import axios from '@/axios';

export default {
  name: 'EnhancedAnalytics',
  components: {
    ReloadOutlined,
    InfoCircleOutlined,
    QuestionCircleOutlined,
  },
  props: {
    eventId: {
      type: String,
      default: ''
    }
  },
  setup(props, { emit }) {
    const store = useStore();
    const route = useRoute();
    const router = useRouter();
    const apis = eventsApis(axios, store);

    // 반응형 화면 크기 감지
    const isMobile = ref(false);
    const isTablet = ref(false);
    const MOBILE_BREAKPOINT = 767;
    const TABLET_BREAKPOINT = 1199;

    const checkScreenSize = () => {
      isMobile.value = window.innerWidth <= MOBILE_BREAKPOINT;
      isTablet.value = window.innerWidth > MOBILE_BREAKPOINT && window.innerWidth <= TABLET_BREAKPOINT;
    };

    // 상태 관리
    const selectedEventId = ref(props.eventId || '');
    const dateRange = ref(null);
    const loading = ref(false);
    const eventsLoading = ref(false);
    const eventList = ref([]);

    // 분석 데이터
    const referrerData = ref(null);
    const funnelData = ref(null);
    const viralData = ref(null);
    const channelData = ref(null);

    // 추천인 테이블 페이지네이션
    const referrerPagination = ref({
      current: 1,
      pageSize: 10,
      showSizeChanger: true,
      pageSizeOptions: ['10', '20', '50', '100'],
      showTotal: (total) => `총 ${total}명`,
    });

    // 로딩 상태
    const referrerLoading = ref(false);
    const funnelLoading = ref(false);
    const viralLoading = ref(false);
    const channelLoading = ref(false);

    // 퍼널 탭 상태
    const activeFunnelTab = ref('certificate');

    // 분기 액션 펼침 상태 관리 (stepName -> boolean)
    const expandedBranchingActions = ref({});

    // 분기 액션 펼침 토글 (아코디언 방식: 한 번에 하나만 열림)
    const toggleBranchingActions = (stepName) => {
      const isCurrentlyExpanded = expandedBranchingActions.value[stepName];
      // 모든 항목 닫기
      expandedBranchingActions.value = {};
      // 현재 항목이 닫혀있었으면 열기
      if (!isCurrentlyExpanded) {
        expandedBranchingActions.value[stepName] = true;
      }
    };

    // 분기 액션 펼침 여부 확인
    const isBranchingExpanded = (stepName) => {
      return expandedBranchingActions.value[stepName] === true;
    };

    // 선택된 액션 상태 관리 (stepName -> Set of actionStepNames)
    const selectedActions = ref({});

    // 액션 선택 여부 확인
    const isActionSelected = (stepName, actionStepName) => {
      return selectedActions.value[stepName]?.has(actionStepName) || false;
    };

    // 액션 선택 토글
    const toggleActionSelection = (stepName, actionStepName) => {
      if (!selectedActions.value[stepName]) {
        selectedActions.value[stepName] = new Set();
      }
      if (selectedActions.value[stepName].has(actionStepName)) {
        selectedActions.value[stepName].delete(actionStepName);
      } else {
        selectedActions.value[stepName].add(actionStepName);
      }
      // 반응성 트리거를 위해 새 객체로 할당
      selectedActions.value = { ...selectedActions.value };
    };

    // 선택된 액션 목록 반환 (미니 퍼널용)
    const getSelectedActionsForStep = (stepName) => {
      const step = currentFunnelSteps.value?.find(s => s.stepName === stepName);
      if (!step?.branchingActions) return [];

      const selectedSet = selectedActions.value[stepName];
      if (!selectedSet || selectedSet.size === 0) return [];

      // 선택된 액션들만 필터링하여 반환
      return step.branchingActions.filter(action => selectedSet.has(action.stepName));
    };

    // 전환율에 따른 CSS 클래스 반환
    const getRateClass = (rate) => {
      if (rate >= 50) return 'high-rate';
      if (rate >= 20) return 'mid-rate';
      return 'low-rate';
    };

    // 퍼널 단계명 매핑 테이블 (기술명 → 사용자 친화적 한글명)
    const stepNameMap = {
      // 알림톡/메시지 관련
      'ALIMTALK_SEND_REQUEST': '알림톡 발송 요청',
      'ALIMTALK_SEND_SUCCESS': '알림톡 발송 성공',
      'ALIMTALK_SEND_FAIL': '알림톡 발송 실패',
      'LMS_FALLBACK': 'LMS 대체 발송',
      'LMS_SEND_SUCCESS': 'LMS 발송 성공',
      'SMS_SEND': 'SMS 발송',

      // 시스템 이벤트
      'SYSTEM:ALIMTALK_SEND_REQUEST': '시스템 알림톡 요청',
      'SYSTEM:ALIMTALK_SEND_SUCCESS': '시스템 알림톡 성공',
      'SYSTEM:ALIMTALK_SEND_LMS_FALLBACK': '시스템 LMS 대체',

      // 추천인/공유 관련
      'REFERRER_SHARE': '추천인 공유',
      'REFERRER_CLICK': '추천 링크 클릭',
      'SHARE_KAKAO': '카카오 공유',
      'SHARE_LINK': '링크 공유',

      // 페이지/사용자 행동
      'PAGE_VIEW': '페이지 조회',
      'PAGE_ENTER': '페이지 진입',
      'PAGE_LEAVE': '페이지 이탈',
      'BUTTON_CLICK': '버튼 클릭',
      'FORM_SUBMIT': '폼 제출',
      'LOGIN': '로그인',
      'LOGOUT': '로그아웃',
      'SIGNUP': '회원가입',

      // 쿠폰/이벤트 관련
      'COUPON_ISSUE': '쿠폰 발급',
      'COUPON_USE': '쿠폰 사용',
      'COUPON_RECEIVED': '쿠폰 수령',
      'EVENT_PARTICIPATE': '이벤트 참여',
      'EVENT_COMPLETE': '이벤트 완료',

      // 인증/등록
      'CERTIFICATE_REQUEST': '인증서 요청',
      'CERTIFICATE_ISSUE': '인증서 발급',
      'CERTIFICATE_REGISTRATION': '인증서 등록',

      // 카카오 채널
      'KAKAO_DIRECT_CHAT': '카카오 1:1 대화',
      'KAKAO_MULTI_CHAT': '카카오 단체 대화',
      'KAKAO_MEMO_CHAT': '카카오 나에게 보내기',

      // 카카오 인증서/공유 관련 (실제 eventmetrics 데이터 기반 추가)
      'KAKAO_CERTIFICATE:VIEW': '카카오 인증서 조회',
      'SHARE:CLICK': '공유 버튼 클릭',
      'SHARE:VIEW': '공유 페이지 조회',
      'QUIZ:CLICK': '퀴즈 시작',
      'QUIZ:FRIEND_REGISTRATION': '친구 퀴즈 등록',
      'KAKAO_SHARE:SHARE': '카카오 공유 완료',

      // 랜딩 페이지 관련
      'LANDING:VIEW': '랜딩 페이지 조회',
      'LANDING:CLICK': 'CTA 버튼 클릭',
      'LANDING:CLICK:CTA_CLICK': 'CTA 버튼 클릭',
      'FRIEND_LANDING:FRIEND_LANDING_VIEW': '친구 랜딩 페이지',

      // 이벤트 이탈 관련
      'FRIEND_EVENT:LEAVE': '친구 이벤트 이탈',
      'PATIENT_EVENT:LEAVE': '환자 이벤트 이탈',
      'REVIEW_EVENT:LEAVE': '리뷰 이벤트 이탈',
    };

    // 퍼널 단계 분류 키워드 (stepName 또는 pageDescription에서 검색)
    // 실제 eventmetrics 데이터 분석 기반 확장 (2025-12-27)
    const certificateKeywords = [
      '알림톡', 'ALIMTALK', 'LMS',
      '카카오 인증서', 'KAKAO_CERTIFICATE',
      '랜딩 페이지에서 조회', 'LANDING_PAGE_VIEW',
      'LANDING', 'CTA', 'SHARE', 'KAKAO_SHARE'
    ];
    // Note: patientKeywords and friendKeywords are no longer used
    // flowType property from backend is used instead for accurate filtering

    // 인증서 알림톡 전환 퍼널 단계 필터링 (funnelType: certificate)
    // 백엔드에서 정의: 발송요청 → 발송성공(알림톡+LMS 합산) → 인증서페이지접속 → 인증서클릭
    const certificateFunnelSteps = computed(() => {
      if (!funnelData.value?.funnels?.[0]?.steps) return [];
      return funnelData.value.funnels[0].steps.filter(step => {
        // 보상 이벤트는 제외
        if (step.isReward) return false;
        // funnelType이 certificate인 단계만 포함
        return step.funnelType === 'certificate';
      });
    });

    // 환자 이벤트페이지 전환 퍼널 단계 필터링 (funnelType: patient)
    const patientFunnelSteps = computed(() => {
      if (!funnelData.value?.funnels?.[0]?.steps) return [];
      return funnelData.value.funnels[0].steps.filter(step => {
        // 보상 이벤트는 제외
        if (step.isReward) return false;
        // funnelType이 patient인 단계만 포함
        return step.funnelType === 'patient';
      });
    });

    // 친구전환 퍼널 단계 필터링 (funnelType: friend)
    const friendFunnelSteps = computed(() => {
      if (!funnelData.value?.funnels?.[0]?.steps) return [];
      return funnelData.value.funnels[0].steps.filter(step => step.funnelType === 'friend' && !step.isReward);
    });

    // 보상 이벤트 필터링 (funnelType: reward 또는 isReward 플래그)
    const rewardFunnelSteps = computed(() => {
      if (!funnelData.value?.funnels?.[0]?.steps) return [];
      return funnelData.value.funnels[0].steps.filter(step => step.funnelType === 'reward' || step.isReward === true);
    });

    // 메인 여정 단계 (보상 이벤트 제외)
    const mainJourneySteps = computed(() => {
      if (!funnelData.value?.funnels?.[0]?.steps) return [];
      return funnelData.value.funnels[0].steps.filter(step => !step.isReward);
    });

    // 현재 선택된 탭에 따른 퍼널 단계
    const currentFunnelSteps = computed(() => {
      if (!funnelData.value?.funnels?.[0]?.steps) return [];

      switch (activeFunnelTab.value) {
        case 'certificate':
          return certificateFunnelSteps.value;
        case 'patient':
          return patientFunnelSteps.value;
        case 'friend':
          return friendFunnelSteps.value;
        case 'reward':
          return rewardFunnelSteps.value;
        default:
          // 기본값: 인증서 알림톡 전환 퍼널
          return certificateFunnelSteps.value;
      }
    });

    // 현재 퍼널 요약 정보 계산
    const currentFunnelSummary = computed(() => {
      const steps = currentFunnelSteps.value;
      if (!steps || steps.length === 0) {
        return {
          totalStartingUsers: 0,
          overallConversionRate: 0,
          biggestDropoffStep: null
        };
      }

      const totalStartingUsers = steps[0]?.uniqueUsers || 0;
      const lastUsers = steps[steps.length - 1]?.uniqueUsers || 0;
      const overallConversionRate = totalStartingUsers > 0
        ? (lastUsers / totalStartingUsers) * 100
        : 0;

      // 가장 큰 이탈 단계 찾기
      let biggestDropoff = { step: '-', dropoffRate: 0 };
      for (let i = 1; i < steps.length; i++) {
        if (steps[i].dropoffCount > biggestDropoff.dropoffRate) {
          biggestDropoff = {
            step: getStepDisplayName(steps[i].stepName, steps[i].pageDescription),
            dropoffRate: steps[i].dropoffCount
          };
        }
      }

      return {
        totalStartingUsers,
        overallConversionRate,
        biggestDropoffStep: biggestDropoff
      };
    });

    // 퍼널 탭 변경 핸들러
    const handleFunnelTabChange = (key) => {
      activeFunnelTab.value = key;
    };

    // 추천인 테이블 페이지네이션 변경 핸들러
    const handleReferrerTableChange = (pagination) => {
      referrerPagination.value = {
        ...referrerPagination.value,
        current: pagination.current,
        pageSize: pagination.pageSize,
      };
    };

    // 단계명을 사용자 친화적 이름으로 변환
    const getStepDisplayName = (stepName, pageDescription) => {
      // pageDescription이 있고, 기술적 이름이 아닌 경우 우선 사용
      if (pageDescription && !pageDescription.includes('ALIMTALK') && !pageDescription.includes('SYSTEM:')) {
        return pageDescription;
      }

      // 매핑 테이블에서 찾기
      if (stepNameMap[stepName]) {
        return stepNameMap[stepName];
      }

      // 부분 매칭 시도 (예: "시스템에서ALIMTALK_SEND_REQUEST" → "ALIMTALK_SEND_REQUEST" 추출)
      for (const [key, value] of Object.entries(stepNameMap)) {
        if (stepName && stepName.includes(key)) {
          return value;
        }
      }

      // 매핑 없으면 원본 반환 (언더스코어를 공백으로 변환하여 가독성 개선)
      if (stepName) {
        return stepName.replace(/_/g, ' ').replace(/:/g, ' ');
      }

      return pageDescription || stepName || '알 수 없음';
    };

    // 테이블 컬럼 정의
    const referrerColumns = [
      {
        title: '추천인',
        key: 'referrerInfo',
        width: 200,
        customRender: ({ record }) => {
          const name = record.referrerName || '-';
          const phone = record.referrerPhone || '';
          if (phone) {
            return `${name} (${phone})`;
          }
          return name !== '-' ? name : record.referrerId?.substring(0, 8) + '...';
        }
      },
      {
        title: () => h('span', [
          '유입',
          h(Tooltip, { title: '이 추천인의 링크를 클릭하여 이벤트 페이지에 방문한 고유 사용자 수' },
            () => h(QuestionCircleOutlined, { style: 'font-size: 12px; color: #999; margin-left: 4px;' })
          )
        ]),
        dataIndex: 'referrerCount', key: 'referrerCount', width: 80,
        sorter: (a, b) => (a.referrerCount || 0) - (b.referrerCount || 0),
        defaultSortOrder: 'descend',
        customRender: ({ record }) => `${record.referrerCount || 0}명`
      },
      {
        title: () => h('span', [
          '응모 완료',
          h(Tooltip, { title: '유입된 사용자 중 실제로 응모를 완료한 고유 사용자 수' },
            () => h(QuestionCircleOutlined, { style: 'font-size: 12px; color: #999; margin-left: 4px;' })
          )
        ]),
        dataIndex: 'successCount', key: 'successCount', width: 100,
        sorter: (a, b) => (a.successCount || 0) - (b.successCount || 0),
        customRender: ({ record }) => `${record.successCount || 0}명`
      },
      {
        title: () => h('span', [
          '응모율',
          h(Tooltip, { title: '유입 대비 응모 완료 비율 (응모 완료 / 유입 × 100%, 최대 100%)' },
            () => h(QuestionCircleOutlined, { style: 'font-size: 12px; color: #999; margin-left: 4px;' })
          )
        ]),
        key: 'conversionRate', width: 120,
        sorter: (a, b) => (a.conversionRate || 0) - (b.conversionRate || 0),
        slots: { customRender: 'conversionRate' }
      },
      {
        title: () => h('span', [
          '2차 공유',
          h(Tooltip, { title: '이 추천인을 통해 유입된 사용자가 본인의 추천 링크로 다시 공유한 횟수' },
            () => h(QuestionCircleOutlined, { style: 'font-size: 12px; color: #999; margin-left: 4px;' })
          )
        ]),
        dataIndex: 'shareCount', key: 'shareCount', width: 100,
        sorter: (a, b) => (a.shareCount || 0) - (b.shareCount || 0),
        customRender: ({ record }) => `${record.shareCount || 0}회`
      },
    ];

    const funnelColumns = [
      {
        title: '단계',
        key: 'stepDisplay',
        width: 200,
        customRender: ({ record }) => {
          return getStepDisplayName(record.stepName, record.pageDescription);
        }
      },
      { title: '순서', dataIndex: 'stepOrder', key: 'stepOrder', width: 80 },
      { title: '사용자 수', dataIndex: 'uniqueUsers', key: 'uniqueUsers', width: 100 },
      { title: '전환율', key: 'conversionRate', width: 150, slots: { customRender: 'conversionRate' } },
      { title: '이탈 수', key: 'dropoffCount', width: 100, slots: { customRender: 'dropoffCount' } },
      { title: '평균 소요 시간', dataIndex: 'averageDuration', key: 'averageDuration', width: 120,
        customRender: ({ record }) => `${(record.averageDuration || 0).toFixed(1)}초` },
    ];

    const channelColumns = [
      {
        title: () => h('span', [
          '채널',
          h(Tooltip, { title: '공유 채널 유형 (metadata.chatType 기반): 1:1 대화, 단체 대화, 나에게 보내기, 알림톡/친구톡 링크' },
            () => h(QuestionCircleOutlined, { style: 'font-size: 12px; color: #999; margin-left: 4px;' })
          )
        ]),
        key: 'channel', width: 120, slots: { customRender: 'channel' }
      },
      {
        title: () => h('span', [
          '사용자 수',
          h(Tooltip, { title: '해당 채널을 통해 유입된 고유 사용자 수 (attendeeId 중복 제거)' },
            () => h(QuestionCircleOutlined, { style: 'font-size: 12px; color: #999; margin-left: 4px;' })
          )
        ]),
        dataIndex: 'uniqueUsers', key: 'uniqueUsers', width: 100
      },
      {
        title: () => h('span', [
          '총 액션',
          h(Tooltip, { title: '해당 채널에서 발생한 모든 이벤트 건수 (중복 포함)' },
            () => h(QuestionCircleOutlined, { style: 'font-size: 12px; color: #999; margin-left: 4px;' })
          )
        ]),
        dataIndex: 'totalActions', key: 'totalActions', width: 100
      },
      {
        title: () => h('span', [
          '전환 수',
          h(Tooltip, { title: "해당 채널에서 result='SUCCESS'인 이벤트 건수" },
            () => h(QuestionCircleOutlined, { style: 'font-size: 12px; color: #999; margin-left: 4px;' })
          )
        ]),
        dataIndex: 'conversionCount', key: 'conversionCount', width: 100
      },
      {
        title: () => h('span', [
          '전환율',
          h(Tooltip, { title: '(전환 수 / 총 액션) * 100. 해당 채널의 액션 대비 전환 비율' },
            () => h(QuestionCircleOutlined, { style: 'font-size: 12px; color: #999; margin-left: 4px;' })
          )
        ]),
        key: 'conversionRate', width: 100, slots: { customRender: 'conversionRate' }
      },
      {
        title: () => h('span', [
          '공유 수',
          h(Tooltip, { title: "해당 채널에서 event='SHARE'인 이벤트 건수" },
            () => h(QuestionCircleOutlined, { style: 'font-size: 12px; color: #999; margin-left: 4px;' })
          )
        ]),
        dataIndex: 'shareCount', key: 'shareCount', width: 100
      },
    ];

    const generationColumns = [
      {
        title: () => h('span', [
          '세대',
          h(Tooltip, { title: '바이럴 전파 깊이 (referralChainDepth). 0세대: 직접 유입, N세대: N-1세대가 공유해서 유입' },
            () => h(QuestionCircleOutlined, { style: 'font-size: 12px; color: #999; margin-left: 4px;' })
          )
        ]),
        dataIndex: 'depth', key: 'depth', width: 80,
        customRender: ({ record }) => `${record.depth}세대`
      },
      {
        title: () => h('span', [
          '사용자 수',
          h(Tooltip, { title: '해당 세대의 고유 사용자 수 (attendeeId 중복 제거)' },
            () => h(QuestionCircleOutlined, { style: 'font-size: 12px; color: #999; margin-left: 4px;' })
          )
        ]),
        dataIndex: 'uniqueUsers', key: 'uniqueUsers', width: 100
      },
      {
        title: () => h('span', [
          '비율',
          h(Tooltip, { title: '(해당 세대 사용자 / 전체 신규 사용자) * 100. 전체 대비 해당 세대의 비중' },
            () => h(QuestionCircleOutlined, { style: 'font-size: 12px; color: #999; margin-left: 4px;' })
          )
        ]),
        dataIndex: 'percentageOfTotal', key: 'percentageOfTotal', width: 100,
        customRender: ({ record }) => `${(record.percentageOfTotal || 0).toFixed(1)}%`
      },
      {
        title: () => h('span', [
          '공유 수',
          h(Tooltip, { title: "해당 세대 사용자들이 한 공유 액션(event='SHARE') 횟수" },
            () => h(QuestionCircleOutlined, { style: 'font-size: 12px; color: #999; margin-left: 4px;' })
          )
        ]),
        dataIndex: 'shareCount', key: 'shareCount', width: 100
      },
    ];

    // 메서드
    const fetchEventList = () => {
      eventsLoading.value = true;

      apis.getEventsWithStatus(
        { status: 'all', limit: 100 },
        (response) => {
          if (response.data) {
            // API 응답 구조에 따라 데이터 추출 (items 배열 사용)
            const events = response.data.items || response.data.data || response.data || [];
            // 이벤트 목록을 eventTitle과 eventStatus를 포함하여 저장
            eventList.value = Array.isArray(events) ? events : [];

            // 고정 옵션 추가 - 써마지쿠폰수령
            const thermageOption = {
              _id: 'THERMAGE',
              eventTitle: '써마지쿠폰수령',
              eventStatus: 'ongoing'
            };
            eventList.value.push(thermageOption);

            console.log('이벤트 목록 로드 완료:', eventList.value.length, '개');

            // 이벤트가 선택되지 않은 경우에만 기본 이벤트 자동 선택
            if (eventList.value.length > 0 && !selectedEventId.value) {
              // 1. 쿼리 파라미터에 eventId가 있으면 해당 이벤트 선택
              const queryEventId = route.query.eventId;
              if (queryEventId) {
                const queryEvent = eventList.value.find(e => e._id === queryEventId);
                if (queryEvent) {
                  selectedEventId.value = queryEventId;
                  emit('event-change', queryEventId);
                  console.log('쿼리 파라미터 이벤트 선택:', queryEvent.eventTitle);
                  fetchAllAnalytics();
                  eventsLoading.value = false;
                  return;
                }
              }

              // 2. 쿼리 파라미터가 없으면 진행중(ongoing) 이벤트 중 가장 최신 것 선택
              const ongoingEvents = eventList.value.filter(e => e.eventStatus === 'ongoing' && e._id !== 'THERMAGE');
              let defaultEvent;
              if (ongoingEvents.length > 0) {
                // createdAt 또는 createAt 기준 내림차순 정렬하여 가장 최신 이벤트 선택
                // (DB 필드명 불일치 대응: createdAt, createAt 둘 다 체크)
                defaultEvent = ongoingEvents.sort((a, b) => {
                  const dateA = new Date(a.createdAt || a.createAt || 0);
                  const dateB = new Date(b.createdAt || b.createAt || 0);
                  return dateB - dateA;
                })[0];
                console.log('가장 최신 진행중 이벤트 선택:', defaultEvent.eventTitle);
              } else {
                // 진행중 이벤트가 없으면 첫 번째 이벤트 선택
                defaultEvent = eventList.value[0];
                console.log('첫 번째 이벤트 자동 선택:', defaultEvent.eventTitle);
              }

              selectedEventId.value = defaultEvent._id;
              emit('event-change', defaultEvent._id);
              fetchAllAnalytics();
            }
          }
          eventsLoading.value = false;
        },
        (error) => {
          console.error('이벤트 목록 로드 실패:', error);
          message.error('이벤트 목록을 불러오는 중 오류가 발생했습니다.');
          eventsLoading.value = false;
        }
      );
    };

    const fetchReferrerPerformance = async () => {
      if (!selectedEventId.value) return;

      referrerLoading.value = true;
      const params = {};
      if (dateRange.value && dateRange.value.length === 2) {
        params.startDate = dateRange.value[0].startOf('day').toISOString();
        params.endDate = dateRange.value[1].endOf('day').toISOString();
      }

      apis.getReferrerPerformance(selectedEventId.value, params,
        (response) => {
          referrerData.value = response.data;
        },
        (error) => {
          console.error('추천인 성과 분석 조회 실패:', error);
          referrerData.value = null;
        }
      ).finally(() => {
        referrerLoading.value = false;
      });
    };

    const fetchFunnelAnalysis = async () => {
      if (!selectedEventId.value) return;

      funnelLoading.value = true;
      const params = {};
      if (dateRange.value && dateRange.value.length === 2) {
        params.startDate = dateRange.value[0].startOf('day').toISOString();
        params.endDate = dateRange.value[1].endOf('day').toISOString();
      }

      apis.getFunnelAnalysis(selectedEventId.value, params,
        (response) => {
          funnelData.value = response.data;
        },
        (error) => {
          console.error('퍼널 분석 조회 실패:', error);
          funnelData.value = null;
        }
      ).finally(() => {
        funnelLoading.value = false;
      });
    };

    const fetchViralAnalytics = async () => {
      if (!selectedEventId.value) return;

      viralLoading.value = true;
      apis.getViralAnalytics(selectedEventId.value,
        (response) => {
          viralData.value = response.data;
        },
        (error) => {
          console.error('바이럴 분석 조회 실패:', error);
          viralData.value = null;
        }
      ).finally(() => {
        viralLoading.value = false;
      });
    };

    const fetchChannelPerformance = async () => {
      if (!selectedEventId.value) return;

      channelLoading.value = true;
      apis.getChannelPerformance(selectedEventId.value,
        (response) => {
          channelData.value = response.data;
        },
        (error) => {
          console.error('채널 성과 분석 조회 실패:', error);
          channelData.value = null;
        }
      ).finally(() => {
        channelLoading.value = false;
      });
    };

    const fetchAllAnalytics = async () => {
      if (!selectedEventId.value) {
        message.warning('이벤트를 선택해주세요.');
        return;
      }

      loading.value = true;
      await Promise.all([
        fetchReferrerPerformance(),
        fetchFunnelAnalysis(),
        fetchViralAnalytics(),
        fetchChannelPerformance(),
      ]);
      loading.value = false;
    };

    const handleEventChange = (value) => {
      selectedEventId.value = value;
      if (value) {
        fetchAllAnalytics();
      }
    };

    const handleDateChange = () => {
      if (selectedEventId.value) {
        fetchReferrerPerformance();
        fetchFunnelAnalysis();
      }
    };

    const resetFilters = () => {
      dateRange.value = null;
      if (selectedEventId.value) {
        fetchAllAnalytics();
      }
    };

    // 이벤트 수정 페이지로 이동
    const goToEditEvent = () => {
      if (!selectedEventId.value) return;
      router.push({
        path: `/events/${selectedEventId.value}/edit`,
        query: {
          from: 'participation',
          tab: 'analytics'
        }
      });
    };

    const filterOption = (input, option) => {
      return option.children[0].children.toLowerCase().indexOf(input.toLowerCase()) >= 0;
    };

    // 유틸리티 함수
    const getEventStatusText = (status) => {
      const statusMap = {
        'draft': '임시저장',
        'preparing': '준비중',
        'ongoing': '진행중',
        'completed': '완료',
        'ended': '종료',
        'scheduled': '예정',
        'active': '진행중',
        'inactive': '비활성'
      };
      return statusMap[status] || status || '알수없음';
    };

    const formatDateRange = (range) => {
      if (!range?.startDate || !range?.endDate) return '-';
      return `${dayjs(range.startDate).format('YYYY-MM-DD')} ~ ${dayjs(range.endDate).format('YYYY-MM-DD')}`;
    };

    const getKFactorStyle = (kFactor) => {
      if (kFactor >= 1) return { color: '#52c41a', fontWeight: 'bold' };
      if (kFactor >= 0.5) return { color: '#faad14' };
      return { color: '#ff4d4f' };
    };

    const getViralityColor = (virality) => {
      if (virality === 'high') return 'green';
      if (virality === 'medium') return 'orange';
      return 'red';
    };

    const getViralityText = (virality) => {
      if (virality === 'high') return '높음 (성장 중)';
      if (virality === 'medium') return '보통';
      return '낮음';
    };

    const getChannelColor = (channel) => {
      const colorMap = {
        // 영문 채널명 (레거시 호환)
        'DirectChat': 'blue',
        'MultiChat': 'purple',
        'MemoChat': 'cyan',
        'OpenChat': 'green',
        'Unknown': 'default',
        // 한글 채널명 (새로운 백엔드 응답)
        '1:1 대화': 'blue',
        '단체 대화': 'purple',
        '나에게 보내기': 'cyan',
        '알림톡/친구톡 링크': 'orange',
      };
      return colorMap[channel] || 'default';
    };

    // 트라페지움 퍼널 설정 (반응형)
    const funnelConfig = computed(() => {
      if (isMobile.value) {
        return {
          width: Math.min(window.innerWidth - 60, 320),  // 모바일: 화면 너비에 맞춤
          stepHeight: 48,      // 각 단계 높이 (작게)
          minWidth: 60,        // 최소 너비
          maxWidth: Math.min(window.innerWidth - 80, 300),  // 최대 너비
          gap: 3,              // 단계 간 간격
        };
      } else if (isTablet.value) {
        return {
          width: 400,          // 태블릿: 중간 크기
          stepHeight: 52,      // 각 단계 높이
          minWidth: 70,        // 최소 너비
          maxWidth: 380,       // 최대 너비
          gap: 4,              // 단계 간 간격
        };
      }
      return {
        width: 500,          // PC: 기본 크기
        stepHeight: 56,      // 각 단계 높이
        minWidth: 80,        // 최소 너비 (마지막 단계)
        maxWidth: 480,       // 최대 너비 (첫 단계)
        gap: 4,              // 단계 간 간격
      };
    });

    const getFunnelHeight = (stepsCount) => {
      return stepsCount * funnelConfig.value.stepHeight + (stepsCount - 1) * funnelConfig.value.gap;
    };

    const getTrapeziumCenterY = (index) => {
      return index * (funnelConfig.value.stepHeight + funnelConfig.value.gap) + funnelConfig.value.stepHeight / 2;
    };

    const getTrapeziumPoints = (index, step, steps) => {
      const { width, stepHeight, minWidth, maxWidth, gap } = funnelConfig.value;
      const centerX = width / 2;
      const y1 = index * (stepHeight + gap);
      const y2 = y1 + stepHeight;

      // 현재 단계와 다음 단계의 너비 계산
      const maxUsers = steps[0]?.uniqueUsers || 1;
      const currentRatio = Math.max(0.15, step.uniqueUsers / maxUsers);
      const nextStep = steps[index + 1];
      const nextRatio = nextStep
        ? Math.max(0.15, nextStep.uniqueUsers / maxUsers)
        : currentRatio * 0.7;

      const topWidth = minWidth + (maxWidth - minWidth) * currentRatio;
      const bottomWidth = minWidth + (maxWidth - minWidth) * nextRatio;

      const topLeft = centerX - topWidth / 2;
      const topRight = centerX + topWidth / 2;
      const bottomLeft = centerX - bottomWidth / 2;
      const bottomRight = centerX + bottomWidth / 2;

      return `${topLeft},${y1} ${topRight},${y1} ${bottomRight},${y2} ${bottomLeft},${y2}`;
    };

    const getStepWidth = (step, steps) => {
      if (!steps || steps.length === 0) return 0;
      const maxUsers = steps[0]?.uniqueUsers || 1;
      return Math.max(5, (step.uniqueUsers / maxUsers) * 100);
    };

    const getFunnelColor = (index, totalSteps) => {
      // 파란색 계열 그라데이션으로 통일 (진한 파랑 → 중간 파랑)
      // 흰색 글자가 보이도록 명도를 50% 이하로 유지
      const total = totalSteps || 10;
      const ratio = index / Math.max(1, total - 1);
      // HSL: 210도(파란색), 채도 70-90%, 명도 30-50% (기존 35-60%에서 조정)
      const lightness = 30 + (ratio * 20);
      const saturation = 90 - (ratio * 15);
      return `hsl(210, ${saturation}%, ${lightness}%)`;
    };

    // props 변경 감지 (immediate: true로 초기 마운트 시에도 실행)
    watch(() => props.eventId, (newEventId, oldEventId) => {
      if (newEventId) {
        // 초기 실행(oldEventId === undefined) 또는 값 변경 시 처리
        if (oldEventId === undefined || newEventId !== selectedEventId.value) {
          selectedEventId.value = newEventId;
          fetchAllAnalytics();
        }
      }
    }, { immediate: true });

    // 생명주기
    onMounted(() => {
      // 화면 크기 체크 및 리스너 등록
      checkScreenSize();
      window.addEventListener('resize', checkScreenSize);

      fetchEventList();
      // fetchAllAnalytics는 watch의 immediate: true로 처리됨
    });

    onUnmounted(() => {
      window.removeEventListener('resize', checkScreenSize);
    });

    return {
      // 반응형 상태
      isMobile,
      isTablet,
      // 데이터 상태
      selectedEventId,
      dateRange,
      loading,
      eventsLoading,
      eventList,
      referrerData,
      referrerPagination,
      funnelData,
      viralData,
      channelData,
      referrerLoading,
      funnelLoading,
      viralLoading,
      channelLoading,
      // 퍼널 탭 관련
      activeFunnelTab,
      certificateFunnelSteps,
      patientFunnelSteps,
      friendFunnelSteps,
      rewardFunnelSteps,
      mainJourneySteps,
      currentFunnelSteps,
      currentFunnelSummary,
      handleFunnelTabChange,
      handleReferrerTableChange,
      // 컬럼 정의
      referrerColumns,
      channelColumns,
      generationColumns,
      fetchReferrerPerformance,
      fetchFunnelAnalysis,
      fetchViralAnalytics,
      fetchChannelPerformance,
      fetchAllAnalytics,
      handleEventChange,
      handleDateChange,
      resetFilters,
      goToEditEvent,
      filterOption,
      getEventStatusText,
      formatDateRange,
      getKFactorStyle,
      getViralityColor,
      getViralityText,
      getChannelColor,
      getStepWidth,
      getFunnelColor,
      funnelConfig,
      getFunnelHeight,
      getTrapeziumCenterY,
      getTrapeziumPoints,
      getStepDisplayName,
      // 분기 액션 펼침 관련
      expandedBranchingActions,
      toggleBranchingActions,
      isBranchingExpanded,
      // 액션 선택 관련
      selectedActions,
      isActionSelected,
      toggleActionSelection,
      getSelectedActionsForStep,
      getRateClass,
    };
  }
};
</script>

<style scoped lang="scss">
/* ===========================================
   반응형 브레이크포인트 정의
   - PC: 1200px 이상
   - 태블릿: 768px ~ 1199px
   - 모바일: 767px 이하
   =========================================== */
$breakpoint-mobile: 767px;
$breakpoint-tablet: 1199px;

@mixin mobile {
  @media screen and (max-width: $breakpoint-mobile) {
    @content;
  }
}

@mixin tablet {
  @media screen and (min-width: 768px) and (max-width: $breakpoint-tablet) {
    @content;
  }
}

@mixin tablet-and-below {
  @media screen and (max-width: $breakpoint-tablet) {
    @content;
  }
}

@mixin pc {
  @media screen and (min-width: 1200px) {
    @content;
  }
}

.enhanced-analytics {
  padding: 0;
}

.filter-section {
  background: #fff;
  padding: 20px;
  border-radius: 8px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);

  @include tablet {
    padding: 16px;
  }

  @include mobile {
    padding: 12px;
    border-radius: 0;
    margin-left: -12px;
    margin-right: -12px;
  }
}

.filter-row {
  margin-bottom: 0;
}

.filter-label {
  display: block;
  margin-bottom: 8px;
  font-weight: 500;
  color: #333;
  font-size: 13px;

  @include mobile {
    font-size: 12px;
    margin-bottom: 6px;
  }
}

.filter-actions {
  @include pc {
    text-align: right;
  }

  @include tablet {
    text-align: left;
    margin-top: 8px;
  }

  @include mobile {
    margin-top: 4px;
  }
}

.mobile-actions {
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 8px;

  :deep(.ant-space-item) {
    width: 100%;
  }
}

.btn-text {
  @include mobile {
    font-size: 13px;
  }
}

.mb-3 {
  margin-bottom: 12px;
}

.mb-4 {
  margin-bottom: 1.5rem !important;

  @include mobile {
    margin-bottom: 1rem !important;
  }
}

.mt-3 {
  margin-top: 12px;
}

.analytics-card {
  height: 100%;

  @include mobile {
    margin-bottom: 0;
  }
}

.analytics-card :deep(.ant-card-head) {
  min-height: 48px;
  padding: 0 16px;

  @include mobile {
    min-height: 40px;
    padding: 0 12px;
  }
}

.analytics-card :deep(.ant-card-head-title) {
  @include mobile {
    font-size: 14px;
  }
}

.analytics-card :deep(.ant-card-body) {
  padding: 16px;

  @include mobile {
    padding: 12px;
  }
}

.virality-indicator {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;

  @include mobile {
    font-size: 13px;
  }
}

/* 모바일 전용 퍼널 카드 레이아웃 */
.funnel-mobile-cards {
  display: none;
  flex-direction: column;
  gap: 0;
  margin: 16px 0;

  @include mobile {
    display: flex;
  }
}

.funnel-step-card {
  position: relative;
  background: #fff;
  border: 1px solid #e8e8e8;
  border-radius: 8px;
  padding: 12px;
  margin-bottom: 8px;
  transition: box-shadow 0.2s ease;

  &:hover {
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  }

  &.first-step {
    border-color: #1890ff;
    background: linear-gradient(135deg, #e6f7ff 0%, #fff 100%);
  }
}

.step-card-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
}

.step-number {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  background: #1890ff;
  color: #fff;
  border-radius: 50%;
  font-size: 12px;
  font-weight: 600;
  flex-shrink: 0;
}

.first-step .step-number {
  background: #52c41a;
}

.step-name {
  font-size: 14px;
  font-weight: 500;
  color: #333;
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.step-card-body {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 8px;
}

.step-users {
  display: flex;
  align-items: baseline;
  gap: 2px;
}

.users-count {
  font-size: 24px;
  font-weight: 700;
  color: #1890ff;
}

.users-label {
  font-size: 14px;
  color: #666;
}

.step-metrics {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.conversion-badge {
  display: inline-block;
  padding: 4px 10px;
  background: #f6ffed;
  border: 1px solid #b7eb8f;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 500;
  color: #52c41a;

  &.low {
    background: #fffbe6;
    border-color: #ffe58f;
    color: #faad14;
  }
}

.dropoff-badge {
  display: inline-block;
  padding: 4px 10px;
  background: #fff2f0;
  border: 1px solid #ffccc7;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 500;
  color: #ff4d4f;
}

.duration-badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 10px;
  background: #fafafa;
  border: 1px solid #d9d9d9;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 500;
  color: #8c8c8c;

  i {
    font-size: 10px;
  }
}

.start-badge {
  display: inline-block;
  padding: 4px 10px;
  background: #e6f7ff;
  border: 1px solid #91d5ff;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 500;
  color: #1890ff;
}

.step-arrow {
  display: flex;
  justify-content: center;
  padding: 4px 0 0 0;
  color: #bfbfbf;
  font-size: 14px;
}

/* PC/태블릿용 트라페지움 퍼널 (데스크톱 전용) */
.trapezium-funnel-container.desktop-only {
  display: flex;
  gap: 16px;
  margin: 16px 0;
  padding: 16px;
  background: #fafafa;
  border-radius: 8px;
  align-items: flex-start;

  @include tablet {
    gap: 12px;
    padding: 12px;
  }

  @include mobile {
    display: none;
  }
}

/* 왼쪽 단계명 패널 (PC/태블릿 전용) */
.funnel-labels-panel {
  flex: 0 0 auto;
  min-width: 280px;
  max-width: 350px;
  display: flex;
  flex-direction: column;

  @include tablet {
    min-width: 220px;
    max-width: 280px;
  }
}

.funnel-label-row {
  display: flex;
  align-items: center;
  padding: 0;
  gap: 8px;
  position: relative;
}

.step-index {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  background: #1890ff;
  color: #fff;
  border-radius: 50%;
  font-size: 11px;
  font-weight: 600;
  flex-shrink: 0;
}

.funnel-label-row:first-child .step-index {
  background: #52c41a;
}

.step-label {
  font-size: 12px;
  font-weight: 500;
  color: #333;
  line-height: 1.3;
  cursor: help;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  flex: 1;
  min-width: 0;

  &:hover {
    color: #1890ff;
  }

  @include tablet {
    font-size: 11px;
  }
}

.step-connector {
  width: 60px;
  height: 2px;
  background: linear-gradient(90deg, #d9d9d9 0%, #d9d9d9 50%, transparent 50%, transparent 100%);
  background-size: 6px 2px;
  flex-shrink: 0;

  @include tablet {
    width: 40px;
  }
}

.step-label-first {
  font-weight: 600;
  color: #1890ff;
}

/* 중앙 퍼널 차트 (PC/태블릿 전용) */
.funnel-chart-wrapper {
  flex: 0 0 auto;
}

.funnel-svg {
  display: block;
}

.funnel-trapezium {
  transition: all 0.3s ease;
  cursor: pointer;
}

.funnel-trapezium:hover {
  filter: brightness(1.1);
}

.funnel-users {
  font-size: 14px;
  fill: #fff;
  font-weight: 700;
  text-shadow: 0 1px 3px rgba(0, 0, 0, 0.5), 0 0 6px rgba(0, 0, 0, 0.3);

  @include tablet {
    font-size: 13px;
  }
}

.step-circle {
  filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.2));
}

.step-number-svg {
  font-size: 11px;
  fill: #fff;
  font-weight: 600;
}

/* 오른쪽 전환율 패널 (PC/태블릿 전용) */
.funnel-metrics-panel {
  flex: 1;
  min-width: 120px;
  display: flex;
  flex-direction: column;
}

.funnel-metric-row {
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 4px 0;
  border-bottom: 1px solid #f0f0f0;
}

.funnel-metric-row:last-child {
  border-bottom: none;
}

.metric-conversion,
.metric-dropoff {
  display: flex;
  align-items: center;
  gap: 8px;
}

.metric-label {
  font-size: 11px;
  color: #999;
  min-width: 36px;
}

.metric-value {
  font-size: 14px;
  font-weight: 600;
  color: #52c41a;
}

.metric-value.start {
  color: #1890ff;
}

.metric-value.dropoff {
  color: #ff4d4f;
  font-size: 12px;
  font-weight: 500;
}

.metric-fraction {
  font-size: 12px;
  font-weight: 400;
  color: #666;
}

.metric-detail {
  display: flex;
  align-items: center;
  gap: 4px;
  margin-top: 2px;
}

.metric-duration {
  display: flex;
  align-items: center;
  gap: 4px;
  margin-top: 4px;
  color: #8c8c8c;
  font-size: 11px;
}

.metric-value.duration {
  color: #8c8c8c;
  font-size: 11px;
  font-weight: 500;
}

/* 퍼널 탭 스타일 */
.funnel-card {
  :deep(.ant-card-body) {
    padding-top: 12px;
  }
}

.funnel-tabs {
  margin-bottom: 16px;

  :deep(.ant-tabs-nav) {
    margin-bottom: 12px;
  }

  :deep(.ant-tabs-tab) {
    padding: 8px 16px;

    @include mobile {
      padding: 6px 10px;
    }
  }

  :deep(.ant-tabs-tab-btn) {
    font-size: 14px;

    @include mobile {
      font-size: 12px;
    }
  }
}

.tab-label {
  display: flex;
  align-items: center;

  i {
    font-size: 14px;

    @include mobile {
      font-size: 12px;
      margin-right: 4px !important;
    }
  }

  @include mobile {
    font-size: 12px;
  }
}

.yellow-button {
  background-color: #faad14 !important;
  border-color: #faad14 !important;
}

.yellow-button:hover {
  background-color: #ffc53d !important;
  border-color: #ffc53d !important;
}

:deep(.ant-statistic-title) {
  font-size: 13px;
  color: #666;

  @include mobile {
    font-size: 12px;
  }
}

:deep(.ant-statistic-content) {
  font-size: 20px;

  @include tablet {
    font-size: 18px;
  }

  @include mobile {
    font-size: 16px;
  }
}

:deep(.ant-table) {
  font-size: 13px;

  @include mobile {
    font-size: 12px;
  }
}

:deep(.ant-table-wrapper) {
  @include mobile {
    overflow-x: auto;
  }
}

:deep(.ant-tag) {
  font-size: 12px;

  @include mobile {
    font-size: 11px;
  }
}

/* 테이블 반응형 스크롤 */
:deep(.ant-table-content) {
  @include mobile {
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
  }
}

/* 카드 제목 툴팁 아이콘 */
:deep(.ant-card-head-title .anticon-question-circle) {
  @include mobile {
    font-size: 12px !important;
  }
}

/* ===========================================
   Fallback 스타일 - 대체 발송 표시
   =========================================== */

/* Fallback 태그 (모바일) */
.fallback-tag {
  display: inline-block;
  padding: 2px 6px;
  margin-left: 6px;
  background: #f9f0ff;
  border: 1px solid #d3adf7;
  border-radius: 4px;
  font-size: 10px;
  font-weight: 500;
  color: #722ed1;
  vertical-align: middle;
}

/* Fallback 태그 (데스크톱) */
.fallback-tag-desktop {
  display: inline-block;
  padding: 1px 4px;
  margin-left: 4px;
  background: #f9f0ff;
  border: 1px solid #d3adf7;
  border-radius: 3px;
  font-size: 9px;
  font-weight: 500;
  color: #722ed1;
  vertical-align: middle;
}

/* Fallback 스텝 카드 (모바일) */
.funnel-step-card.fallback-step {
  border-color: #d3adf7;
  background: linear-gradient(135deg, #f9f0ff 0%, #fff 100%);

  .step-number {
    background: #722ed1;
  }
}

/* Fallback 배지 */
.fallback-badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 10px;
  background: #f9f0ff;
  border: 1px solid #d3adf7;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 500;
  color: #722ed1;

  i {
    font-size: 11px;
  }
}

/* Fallback 전환 배지 */
.conversion-badge.fallback {
  background: #f9f0ff;
  border-color: #d3adf7;
  color: #722ed1;
}

/* Fallback 라벨 행 (PC) */
.fallback-label-row {
  .step-index.fallback-index {
    background: #722ed1;
  }

  .step-label.step-label-fallback {
    color: #722ed1;
  }
}

/* Fallback 메트릭 행 (PC) */
.fallback-metric-row {
  background: rgba(114, 46, 209, 0.03);
  border-radius: 4px;
}

.metric-value.fallback-value {
  color: #722ed1;
}

.metric-fallback-info {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 11px;
  color: #722ed1;
  margin-top: 2px;

  i {
    font-size: 10px;
  }
}

/* Fallback 테이블 행 */
:deep(.fallback-table-row) {
  background: rgba(114, 46, 209, 0.03) !important;

  &:hover > td {
    background: rgba(114, 46, 209, 0.06) !important;
  }
}

.conversion-cell {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.fallback-table-tag {
  font-size: 10px;
  padding: 0 4px;
  height: 18px;
  line-height: 16px;
}

/* ===========================================
   Reward 스타일 - 보상 이벤트 표시
   =========================================== */

/* Reward 탭 라벨 스타일 */
.reward-tab-label {
  color: #d48806;
}

/* Reward 태그 (모바일) */
.reward-tag {
  display: inline-block;
  padding: 2px 6px;
  margin-left: 6px;
  background: #fffbe6;
  border: 1px solid #ffe58f;
  border-radius: 4px;
  font-size: 10px;
  font-weight: 500;
  color: #d48806;
  vertical-align: middle;
}

/* Reward 태그 (데스크톱) */
.reward-tag-desktop {
  display: inline-block;
  padding: 1px 4px;
  margin-left: 4px;
  background: #fffbe6;
  border: 1px solid #ffe58f;
  border-radius: 3px;
  font-size: 9px;
  font-weight: 500;
  color: #d48806;
  vertical-align: middle;
}

/* Reward 스텝 카드 (모바일) */
.funnel-step-card.reward-step {
  border-color: #ffe58f;
  background: linear-gradient(135deg, #fffbe6 0%, #fff 100%);

  .step-number {
    background: #d48806;
  }
}

/* Reward 배지 */
.reward-badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 10px;
  background: #fffbe6;
  border: 1px solid #ffe58f;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 500;
  color: #d48806;

  i {
    font-size: 11px;
  }
}

/* Reward 전환 배지 */
.conversion-badge.reward {
  background: #fffbe6;
  border-color: #ffe58f;
  color: #d48806;
}

/* Reward 라벨 행 (PC) */
.reward-label-row {
  .step-index.reward-index {
    background: #d48806;
  }

  .step-label.step-label-reward {
    color: #d48806;
  }
}

/* Reward 메트릭 행 (PC) */
.reward-metric-row {
  background: rgba(212, 136, 6, 0.03);
  border-radius: 4px;
}

.metric-value.reward-value {
  color: #d48806;
}

.metric-reward-info {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 11px;
  color: #d48806;
  margin-top: 2px;

  i {
    font-size: 10px;
  }
}

/* Reward 테이블 행 */
:deep(.reward-table-row) {
  background: rgba(212, 136, 6, 0.03) !important;

  &:hover > td {
    background: rgba(212, 136, 6, 0.06) !important;
  }
}

.reward-table-tag {
  font-size: 10px;
  padding: 0 4px;
  height: 18px;
  line-height: 16px;
}

/* ===========================================
   분기 액션 펼침 패널 스타일
   =========================================== */

/* 분기 액션 토글 버튼 */
.branching-toggle-btn {
  font-size: 11px;
  padding: 2px 8px;
  height: auto;
  line-height: 1.4;
  color: #1890ff;
  margin-left: 8px;

  &:hover {
    color: #40a9ff;
  }

  i {
    font-size: 10px;
    transition: transform 0.2s ease;
  }
}

/* 분기 액션 펼침 패널 */
.branching-actions-panel {
  margin-top: 8px;
  margin-left: 30px;
  padding: 16px 20px;
  background: linear-gradient(135deg, #f6f9fc 0%, #f0f5fa 100%);
  border: 1px solid #e1e8ed;
  border-radius: 8px;
  border-left: 3px solid #1890ff;
  min-width: 450px;
  max-width: 600px;

  @include tablet {
    min-width: 380px;
    max-width: 500px;
  }

  @include mobile {
    margin-left: 0;
    padding: 12px 14px;
    min-width: auto;
    max-width: none;
  }
}

.branching-actions-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
  font-size: 13px;
  font-weight: 500;
  color: #595959;

  i {
    color: #8c8c8c;
  }
}

.branching-header-left {
  display: flex;
  align-items: center;
  gap: 4px;
}

.branching-close-btn {
  color: #8c8c8c;
  padding: 4px 8px;
  height: auto;
  border-radius: 4px;

  &:hover {
    color: #ff4d4f;
    background: #fff2f0;
  }

  i {
    font-size: 12px;
  }
}

.branching-actions-list {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.branching-action-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 12px;
  background: #fff;
  border: 1px solid #e8e8e8;
  border-radius: 6px;
  transition: all 0.2s ease;

  &:hover {
    border-color: #1890ff;
    box-shadow: 0 2px 4px rgba(24, 144, 255, 0.1);
  }

  @include mobile {
    flex-wrap: wrap;
    gap: 4px;
  }
}

.action-left {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
  min-width: 0;
}

.action-index {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  background: #e6f7ff;
  color: #1890ff;
  border-radius: 50%;
  font-size: 10px;
  font-weight: 600;
  flex-shrink: 0;
}

.action-name {
  font-size: 12px;
  color: #333;
  font-weight: 500;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  @include mobile {
    font-size: 11px;
    white-space: normal;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    display: -webkit-box;
    overflow: hidden;
  }
}

.action-right {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-shrink: 0;

  @include mobile {
    width: 100%;
    justify-content: flex-end;
  }
}

.action-users {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  color: #666;

  i {
    font-size: 10px;
    color: #8c8c8c;
  }

  strong {
    color: #1890ff;
    font-weight: 600;
  }
}

.action-rate {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 10px;
  font-size: 11px;
  font-weight: 600;

  &.high-rate {
    background: #f6ffed;
    color: #52c41a;
    border: 1px solid #b7eb8f;
  }

  &.mid-rate {
    background: #fffbe6;
    color: #faad14;
    border: 1px solid #ffe58f;
  }

  &.low-rate {
    background: #fff2f0;
    color: #ff4d4f;
    border: 1px solid #ffccc7;
  }

  &.non-conversion {
    background: #f5f5f5;
    color: #8c8c8c;
    border: 1px solid #d9d9d9;
  }

  .rate-detail {
    font-size: 10px;
    font-weight: 400;
    color: #8c8c8c;
    margin-left: 4px;
  }

  .non-conversion-badge {
    display: inline-block;
    background: #8c8c8c;
    color: white;
    font-size: 9px;
    padding: 1px 4px;
    border-radius: 4px;
    margin-left: 4px;
  }
}

/* ===========================================
   우측 페이지 내 액션 버튼 스타일
   =========================================== */

.branching-actions-panel-right {
  display: flex;
  flex-direction: column;
  min-width: 160px;
  margin-left: 16px;

  @include tablet {
    min-width: 140px;
    margin-left: 12px;
  }

  @include mobile {
    display: none;
  }
}

.branching-metric-row {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  padding: 4px 0;
}

.branching-toggle-btn-right {
  font-size: 11px;
  padding: 4px 10px;
  height: auto;
  line-height: 1.4;
  color: #1890ff;
  background: #e6f7ff;
  border: 1px solid #91d5ff;
  border-radius: 12px;
  transition: all 0.2s ease;

  &:hover, &.active {
    color: #fff;
    background: #1890ff;
    border-color: #1890ff;
  }

  i {
    font-size: 10px;
    transition: transform 0.2s ease;
  }
}

/* ===========================================
   하단 확장 패널 (페이지 내 액션 상세)
   =========================================== */

.branching-detail-section {
  margin-top: 20px;
  padding: 20px;
  background: linear-gradient(135deg, #f8fafc 0%, #f0f5fa 100%);
  border: 1px solid #e1e8ed;
  border-radius: 12px;
  border-left: 4px solid #1890ff;
}

.branching-detail-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
  padding-bottom: 12px;
  border-bottom: 1px solid #e8e8e8;
}

.branching-detail-title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  font-weight: 600;
  color: #333;

  i {
    color: #1890ff;
    font-size: 16px;
  }
}

.branching-detail-ref {
  font-size: 12px;
  color: #8c8c8c;
  font-weight: 400;
  margin-left: 8px;
}

.branching-detail-content {
  display: flex;
  gap: 24px;

  @include mobile {
    flex-direction: column;
  }
}

/* 왼쪽: 액션 선택 패널 */
.action-selector-panel {
  flex: 1;
  min-width: 280px;
}

.action-selector-title {
  font-size: 12px;
  font-weight: 600;
  color: #666;
  margin-bottom: 12px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.action-selector-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.action-selector-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  background: #fff;
  border: 1px solid #e8e8e8;
  border-radius: 8px;
  transition: all 0.2s ease;
  cursor: pointer;

  &:hover {
    background: #f0f7ff;
    border-color: #91d5ff;
  }

  &.selected {
    background: #e6f7ff;
    border-color: #1890ff;
    box-shadow: 0 0 0 2px rgba(24, 144, 255, 0.1);
  }
}

.action-selector-name {
  flex: 1;
  font-size: 13px;
  color: #333;
  font-weight: 500;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.action-selector-bar-wrapper {
  width: 60px;
  height: 6px;
  background: #e8e8e8;
  border-radius: 3px;
  overflow: hidden;
}

.action-selector-bar {
  height: 100%;
  border-radius: 3px;
  transition: width 0.3s ease;

  &.high-rate { background: linear-gradient(90deg, #52c41a, #73d13d); }
  &.mid-rate { background: linear-gradient(90deg, #faad14, #ffc53d); }
  &.low-rate { background: linear-gradient(90deg, #1890ff, #40a9ff); }
  &.non-conversion { background: linear-gradient(90deg, #8c8c8c, #bfbfbf); }
}

.action-selector-stats {
  font-size: 12px;
  color: #666;
  white-space: nowrap;
  min-width: 80px;
  text-align: right;
}

.action-selector-rate {
  color: #999;
}

.exit-badge {
  display: inline-block;
  background: #ff4d4f;
  color: #fff;
  font-size: 9px;
  padding: 1px 4px;
  border-radius: 4px;
  margin-left: 4px;
}

/* 오른쪽: 미니 퍼널 패널 */
.mini-funnel-panel {
  flex: 1;
  min-width: 280px;
  background: #fff;
  border: 1px solid #e8e8e8;
  border-radius: 8px;
  padding: 16px;
}

.mini-funnel-title {
  font-size: 12px;
  font-weight: 600;
  color: #666;
  margin-bottom: 16px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.mini-funnel-chart {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.mini-funnel-step {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.mini-step-connector {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 4px 0;
  color: #8c8c8c;

  i {
    font-size: 12px;
  }
}

.mini-step-rate {
  font-size: 12px;
  font-weight: 600;
  padding: 2px 8px;
  border-radius: 10px;

  &.high-rate { background: #f6ffed; color: #52c41a; }
  &.mid-rate { background: #fffbe6; color: #faad14; }
  &.low-rate { background: #e6f7ff; color: #1890ff; }
}

.mini-step-bar-wrapper {
  height: 32px;
  background: #f5f5f5;
  border-radius: 6px;
  overflow: hidden;
  position: relative;
}

.mini-step-bar {
  height: 100%;
  border-radius: 6px;
  transition: width 0.4s ease;

  &.start { background: linear-gradient(90deg, #1890ff, #40a9ff); }
  &.high-rate { background: linear-gradient(90deg, #52c41a, #73d13d); }
  &.mid-rate { background: linear-gradient(90deg, #faad14, #ffc53d); }
  &.low-rate { background: linear-gradient(90deg, #1890ff, #40a9ff); }
}

.mini-step-info {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 4px;
}

.mini-step-name {
  font-size: 12px;
  color: #333;
  font-weight: 500;
}

.mini-step-users {
  font-size: 13px;
  color: #1890ff;
  font-weight: 600;
}

.start-step .mini-step-name {
  color: #1890ff;
}

.mini-funnel-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  color: #bfbfbf;
  text-align: center;

  i {
    font-size: 32px;
    margin-bottom: 12px;
  }

  span {
    font-size: 13px;
    line-height: 1.6;
  }
}

/* Legacy: keep for backwards compatibility */
.action-bar-left {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
  min-width: 0;
}

.action-checkbox {
  flex-shrink: 0;
}

.action-bar-name {
  font-size: 12px;
  color: #333;
  font-weight: 500;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.action-bar-right {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
}

.action-bar-wrapper {
  width: 80px;
  height: 8px;
  background: #e8e8e8;
  border-radius: 4px;
  overflow: hidden;
}

.action-bar-fill {
  height: 100%;
  border-radius: 4px;
  transition: width 0.3s ease;

  &.high-rate {
    background: linear-gradient(90deg, #52c41a 0%, #73d13d 100%);
  }

  &.mid-rate {
    background: linear-gradient(90deg, #faad14 0%, #ffc53d 100%);
  }

  &.low-rate {
    background: linear-gradient(90deg, #1890ff 0%, #40a9ff 100%);
  }

  &.non-conversion {
    background: linear-gradient(90deg, #8c8c8c 0%, #bfbfbf 100%);
  }
}

.action-bar-stats {
  font-size: 11px;
  color: #666;
  white-space: nowrap;
  min-width: 90px;
  text-align: right;
}
</style>
